/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import EmObject, { action, computed, set } from '@ember/object';
import { alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import config from 'cio/config/environment';
import AccountService from 'cio/services/account';
import MasqService from 'cio/services/masq';
import ApiService from 'cio/services/api';
import FormValidations from 'cio/utils/form-validations';
import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import { timeout, task } from 'ember-concurrency';

const DEBOUNCE_DURATION = config.APP.debounceDuration;

const Validations = {
  accountID(value: string) {
    let numberValue = Number(value.trim());

    if (!numberValue || numberValue < 0 || !Number.isInteger(numberValue)) {
      return 'Enter valid account id';
    }

    return true;
  },
};

interface MasqueradeBannerFormArgs {
  inputLabel: string;
  autofocus?: boolean;
}

export default class MasqueradeBannerForm extends Component<MasqueradeBannerFormArgs> {
  @service account!: AccountService;
  @service masq!: MasqService;
  @service api!: ApiService;

  @tracked _formValues: { accountID: string };
  _validations!: FormValidations;

  @tracked _accountFetchError: string | null = null;

  @alias('fetchAccountInfoTask.last.value') masqAccountInfo!: ReturnType<
    MasqueradeBannerForm['fetchAccountInfoTask']['perform']
  >;

  @computed('_formValues.accountID', '_validations.errors.accountID', '_accountFetchError')
  get accountIDInputErrors() {
    if (this._formValues.accountID && this._validations.errors.accountID) {
      return this._validations.errors.accountID;
    } else if (this._accountFetchError) {
      return [this._accountFetchError];
    }

    return;
  }

  constructor(owner: unknown, args: MasqueradeBannerFormArgs) {
    super(owner, args);

    this._validations = FormValidations.create({ validations: Validations, context: this, modelKey: '_formValues' });
    this._formValues = EmObject.create({ accountID: '' });
  }

  fetchAccountInfoTask = task({ restartable: true }, async () => {
    if (this._validations.errors.accountID) {
      return null;
    }

    let accountID = this._formValues.accountID.trim();

    if (!Ember.testing) {
      await timeout(DEBOUNCE_DURATION);
    }

    // fetch account to masq into, allow submitting only if it's found:
    // use API service instead of Ember Data to avoid loading records from other accounts into data store,
    // which would need to get removed later, could break a lot of things
    let account;
    try {
      let res = await this.api.request(`accounts/${accountID}`, {
        pathScope: 'global',
        ignoreErrors: true,
      });
      account = res.account;
    } catch (e) {
      let errorCode = e.errors[0]?.status;
      switch (errorCode) {
        case '404':
          this._accountFetchError = `Account ${accountID} not found`;
          break;
        case '403':
          this._accountFetchError = `You do not have permission to masq into account ${accountID}`;
          break;
        default:
          this._accountFetchError = `Error loading account ${accountID} info`;
      }
      return null;
    }

    return {
      id: account.id,
      name: account.name,
      dataCenter: account.data_center,
    };
  });

  @action
  onAccountIDInput(id: string) {
    this._accountFetchError = null;
    set(this._formValues, 'accountID', id);

    this._validations.validateAttribute('accountID');

    return this.fetchAccountInfoTask.perform();
  }

  @action
  masquerade(e: Event) {
    e.preventDefault();
    return this.masq.masqueradeInto({
      accountID: this._formValues.accountID.trim(),
    });
  }
}
